/**
 * Rascunho e situação da referência do cartão da audiometria (#461). Vivem fora do componente
 * porque a aba precisa deles: o cartão desmonta quando o exame sai de "Concluído", e o chip
 * "Referência" do cabeçalho depende da caixa marcada lá dentro.
 */
import type { AppointmentExam } from '../../../types/appointmentExam';
import type { AudiometryEar } from '../../../types/audiometry';
import { mesmoId } from '../../../utils/audiometria';
import type { ContextoDoTrabalhador } from './useContextoDoTrabalhador';

export type GridDraft = Record<AudiometryEar, Record<string, string>>;

/**
 * O que a pessoa digitou e marcou e ainda não foi salvo. `null` em cada campo = a pessoa não
 * mexeu; valem os limiares gravados e o padrão do contexto.
 */
export interface RascunhoDaAudiometria {
  grade: GridDraft | null;
  referenceChoice: boolean | null;
}

export const RASCUNHO_VAZIO: RascunhoDaAudiometria = { grade: null, referenceChoice: null };

export interface SituacaoDaReferencia {
  isReference: boolean;
  hasEffectiveReference: boolean;
  /** Outro exame do trabalhador é a referência efetiva hoje. */
  otherReferenceExists: boolean;
  referenceChecked: boolean;
}

export function situacaoDaReferencia(
  exam: AppointmentExam,
  context: ContextoDoTrabalhador,
  rascunho: RascunhoDaAudiometria,
): SituacaoDaReferencia {
  const record = exam.audiometry ?? null;
  const history = context.history;
  const isReference = record?.isReference ?? false;
  const hasEffectiveReference = history?.effectiveReference ?? false;
  const otherReferenceExists =
    !isReference &&
    (hasEffectiveReference ||
      (record?.comparison != null && !mesmoId(record.comparison.referenceExamId, exam.id)));
  // Primeira audiometria: sem referência efetiva e histórico já conhecido, a caixa vem marcada.
  const defaultChecked = isReference || (history !== null && !hasEffectiveReference);
  const referenceChecked = isReference ? true : (rascunho.referenceChoice ?? defaultChecked);
  return { isReference, hasEffectiveReference, otherReferenceExists, referenceChecked };
}

/**
 * Chip "Referência" do cabeçalho: já é a referência, ou será (caixa marcada e nenhuma outra
 * referência efetiva). Sem histórico não dá para afirmar que não há outra: não aparece.
 */
export function mostraChipDeReferencia(
  exam: AppointmentExam,
  context: ContextoDoTrabalhador,
  rascunho: RascunhoDaAudiometria,
): boolean {
  const situacao = situacaoDaReferencia(exam, context, rascunho);
  if (situacao.isReference) return true;
  return context.history !== null && situacao.referenceChecked && !situacao.otherReferenceExists;
}
